/**
 * Chart Types and Interfaces
 *
 * Shared type definitions used across the chart subsystem.
 *
 * Key types:
 * - `TimeframeType`     — supported timeframe strings ("1" … "1D")
 * - `LayoutMode`        — multi-panel layout modes (tab / grid / horizontal / vertical)
 * - `ChartType`         — visual style (candlestick / line / area / baseline / bar)
 * - `TickData`          — raw tick payload from the WebSocket
 * - `CandleData`        — OHLCV bar used by lightweight-charts
 * - `ChartSymbol`       — instrument reference (symbol, segment, id)
 * - `ChartPanel`        — Redux state for one chart pane
 * - `OneMinuteBar`      — 1-min aggregation buffer
 * - `TIMEFRAME_CONFIGS` — maps timeframe key → minutes + display label
 * - `BackfillRequest/Response` — REST backfill API contract
 */
export type BuiltInTimeframe = "1" | "3" | "5" | "7" | "10" | "12" | "15" | "20" | "30" | "1D";
export type TimeframeType = BuiltInTimeframe | (string & {});
export type LayoutMode = "grid" | "horizontal" | "vertical" | "tab";
export type ChartType = "candlestick" | "line" | "area" | "baseline" | "bar";

export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  candlestick: "Candlestick",
  line: "Line",
  area: "Area",
  baseline: "Baseline",
  bar: "Bar (OHLC)",
};

export interface TickData {
  symbol: string;
  ltp: number;
  volume?: number;
  oi?: number;
  timestamp: number; // milliseconds
}

export interface CandleData {
  time: number; // Unix timestamp in seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  oi?: number;
}

export interface ChartSymbol {
  symbol: string;
  segment?: string;
  name?: string;
  id?: number; // numeric scripId used for backfill/todayfill API calls
}

export interface ChartPanel {
  id: string;
  symbol: ChartSymbol | null;
  timeframe: TimeframeType;
  chartType: ChartType;
  data: CandleData[];
  isLoading: boolean;
  error: string | null;
}

export interface OneMinuteBar {
  timestamp: number; // Start of minute in ms
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  oi: number;
  tickCount: number;
}

/** A single EOD (end-of-day) bar from the server — one per calendar trading day. */
export interface DayBar {
  timestamp: number; // UTC-equivalent of IST day-start in ms (= IST_midnight_ms - IST_OFFSET_MS)
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  oi: number;
}

export interface TimeframeConfig {
  minutes: number;
  label: string;
}

export const TIMEFRAME_CONFIGS: Record<string, TimeframeConfig> = {
  "1": { minutes: 1, label: "1m" },
  "3": { minutes: 3, label: "3m" },
  "5": { minutes: 5, label: "5m" },
  "7": { minutes: 7, label: "7m" },
  "10": { minutes: 10, label: "10m" },
  "12": { minutes: 12, label: "12m" },
  "15": { minutes: 15, label: "15m" },
  "20": { minutes: 20, label: "20m" },
  "30": { minutes: 30, label: "30m" },
  // Day-based timeframes — use EOD data source (not 1-minute bars)
  "1D":  { minutes: 1440,     label: "1D"  },
  "1W":  { minutes: 10080,    label: "1W"  },
  "1Mo": { minutes: 43800,    label: "1Mo" },
  "6Mo": { minutes: 262800,   label: "6Mo" },
  "1Y":  { minutes: 525600,   label: "1Y"  },
  "10Y": { minutes: 5256000,  label: "10Y" },
};

export const BUILT_IN_TIMEFRAMES: string[] = ["1", "3", "5", "7", "10", "12", "15", "20", "30", "1D", "1W", "1Mo", "6Mo", "1Y"];

/**
 * EOD (day-based) timeframes — these use a different data source (EOD bars, not 1-minute bars).
 * Includes 1D and all higher-period timeframes computed from daily candles.
 */
export const EOD_TIMEFRAMES = ["1D", "1W", "1Mo", "6Mo", "1Y"] as const;
export type EodTimeframe = (typeof EOD_TIMEFRAMES)[number];

/** Returns true when the timeframe should use EOD (end-of-day) bar data instead of 1-minute bars. */
export const isDayBasedTimeframe = (tf: TimeframeType): boolean =>
  (EOD_TIMEFRAMES as readonly string[]).includes(tf) ||
  /^\d+(D|W|Mo|Y)$/.test(tf);

/** Get config for any timeframe (built-in or custom). Returns a fallback for unknown keys. */
export const getTimeframeConfig = (tf: TimeframeType): TimeframeConfig => {
  return TIMEFRAME_CONFIGS[tf] || { minutes: 1, label: tf };
};

/** Register a custom timeframe at runtime so convertTimeframe can look it up. */
export const registerCustomTimeframe = (key: string, config: TimeframeConfig) => {
  TIMEFRAME_CONFIGS[key] = config;
};

/** Unregister a custom timeframe. */
export const unregisterCustomTimeframe = (key: string) => {
  if (!BUILT_IN_TIMEFRAMES.includes(key)) {
    delete TIMEFRAME_CONFIGS[key];
  }
};

export interface BackfillRequest {
  symbol: string;
  segment?: string;
  from: number; // Unix timestamp in seconds
  to: number; // Unix timestamp in seconds
}

export interface BackfillResponse {
  symbol: string;
  data: CandleData[];
  success: boolean;
  error?: string;
}
